import { AppThunk } from '../store/types';
import { EExerciseStatus, ExercisesRest, TCefrLevel, TExerciseAnswerValue, TExerciseNative } from '../../common/rest/exercises/exercisesRest';
import {
	clearExerciseAnswersAction,
	removeExerciseAnswerAction,
	setExerciseAction,
	setExerciseAnswerAction,
	setExerciseGeneratingAction,
	setExercisesDataAction,
	setExercisesLoadFailedAction,
	setExerciseStudentsAction,
	setExerciseStatusAction,
	setExerciseGenerateFailedAction,
	setExerciseNativeAction,
} from '../store/exercises/actions';
import { setSnackbarPanelAction } from '../store/general/actions';
import { getAudioExercises, getExerciseNatives, isExerciseNativeFor, sameExerciseContent } from '../store/exercises/selectors';
import { hasTranslatableText } from '../../common/utils/exerciseContentUtil';
import { getGroups } from '../store/models/selectors';
import { updateAudioAction } from '../store/models/actions';
import { TAudio } from '../store/models/types';

export class ExercisesEffects {

	// langCode: the viewer's native language — stored translations come along, so a reload shows them at once
	public static load(audioId: number, langCode?: string): AppThunk {
		return async (dispatch) => {
			try {
				dispatch(setExercisesLoadFailedAction(audioId, false));
				const { natives, ...data } = await ExercisesRest.fetch(audioId, langCode);
				// before the data, so the tab never sees an exercise without its translation and asks for it
				if (natives && langCode) {
					data.exercises.forEach(e => {
						if (natives[e.id]) dispatch(setExerciseNativeAction(e.id, { langCode, content: e.content, data: natives[e.id] }));
					});
				}
				dispatch(setExercisesDataAction(audioId, data));
				dispatch(ExercisesEffects.syncListProgress(audioId));
			} catch (e) {
				console.error('ExercisesEffects.load failed', e);
				dispatch(setExercisesLoadFailedAction(audioId, true));
			}
		}
	}

	public static generate(audioId: number, promptId: number, level?: TCefrLevel): AppThunk {
		return async (dispatch, getState) => {
			dispatch(setExerciseGeneratingAction(audioId, promptId, true));
			dispatch(setExerciseGenerateFailedAction(audioId, promptId, false));
			try {
				const { id, content } = await ExercisesRest.generate(audioId, promptId, level);
				dispatch(setExerciseAction(audioId, { id, promptId, content, status: EExerciseStatus.ACTIVE }));
				dispatch(ExercisesEffects.syncListProgress(audioId));
			} catch (e) {
				console.error('ExercisesEffects.generate failed', e);
				// a missing exercise shows the failure inline (with Try again); a failed regenerate keeps the old
				// content on screen, so there the only place to say it is a snackbar
				const existed = !!getAudioExercises(getState(), audioId)?.exercises.some(ex => ex.promptId === promptId);
				if (existed) {
					dispatch(setSnackbarPanelAction(true, 'Failed to regenerate the exercise'));
				} else {
					dispatch(setExerciseGenerateFailedAction(audioId, promptId, true));
				}
			} finally {
				dispatch(setExerciseGeneratingAction(audioId, promptId, false));
			}
		}
	}

	// The exercise's instruction/questions/explanations in the viewer's native language — once per content.
	public static loadNative(audioId: number, exerciseId: number, langCode: string): AppThunk {
		return async (dispatch, getState) => {
			const content = getAudioExercises(getState(), audioId)?.exercises.find(e => e.id === exerciseId)?.content;
			if (!content || !hasTranslatableText(content) || isExerciseNativeFor(getExerciseNatives(getState())[exerciseId], content, langCode)) return;
			dispatch(setExerciseNativeAction(exerciseId, { langCode, content, data: null }));
			let data: TExerciseNative = { items: [] };
			try {
				const response = await ExercisesRest.fetchNative(exerciseId, langCode);
				// translated from other content (regenerated meanwhile) — show none
				if (response.native && sameExerciseContent(response.content, content)) data = response.native;
			} catch (e) {
				console.error('ExercisesEffects.loadNative failed', e);
			}
			// a newer request owns the slot — overwriting its loading mark would just trigger a repeat request
			if (!isExerciseNativeFor(getExerciseNatives(getState())[exerciseId], content, langCode)) return;
			dispatch(setExerciseNativeAction(exerciseId, { langCode, content, data }));
		}
	}

	// Optimistic: the picked answer is stored before the request settles so the UI reacts instantly;
	// the server-computed `correct` then replaces the (absent) client guess once it arrives.
	public static saveAnswer(audioId: number, exerciseId: number, questionIndex: number, answer: TExerciseAnswerValue): AppThunk {
		return async (dispatch) => {
			dispatch(setExerciseAnswerAction(audioId, { exerciseId, questionIndex, answer, correct: false }));
			try {
				const { correct } = await ExercisesRest.saveAnswer(exerciseId, questionIndex, answer);
				dispatch(setExerciseAnswerAction(audioId, { exerciseId, questionIndex, answer, correct }));
				dispatch(ExercisesEffects.syncListProgress(audioId));
			} catch (e) {
				console.error('ExercisesEffects.saveAnswer failed', e);
				// roll back the optimistic answer — otherwise the question is stuck "answered" forever and can't be retried
				dispatch(removeExerciseAnswerAction(audioId, exerciseId, questionIndex));
				dispatch(ExercisesEffects.syncListProgress(audioId));
				dispatch(setSnackbarPanelAction(true, 'Failed to save the answer'));
			}
		}
	}

	// userId: reset that participant's answers (teacher/admin view); omitted = my own
	public static clearAnswers(audioId: number, exerciseId: number, userId?: number): AppThunk {
		return async (dispatch) => {
			if (!userId) {
				dispatch(clearExerciseAnswersAction(audioId, exerciseId));
				dispatch(ExercisesEffects.syncListProgress(audioId));
			}
			try {
				await ExercisesRest.clearAnswers(exerciseId, userId);
				if (userId) dispatch(ExercisesEffects.loadStudents(audioId));
			} catch (e) {
				console.error('ExercisesEffects.clearAnswers failed', e);
				dispatch(setSnackbarPanelAction(true, 'Failed to clear answers'));
			}
		}
	}

	// teacher/admin: take an exercise out of the lesson (INACTIVE) or put it back (ACTIVE)
	public static setStatus(audioId: number, exerciseId: number, status: EExerciseStatus): AppThunk {
		return async (dispatch) => {
			dispatch(setExerciseStatusAction(audioId, exerciseId, status));
			dispatch(ExercisesEffects.syncListProgress(audioId));
			try {
				await ExercisesRest.setStatus(exerciseId, status);
			} catch (e) {
				console.error('ExercisesEffects.setStatus failed', e);
				const previous = status === EExerciseStatus.ACTIVE ? EExerciseStatus.INACTIVE : EExerciseStatus.ACTIVE;
				dispatch(setExerciseStatusAction(audioId, exerciseId, previous));
				dispatch(ExercisesEffects.syncListProgress(audioId));
				dispatch(setSnackbarPanelAction(true, 'Failed to update the exercise'));
			}
		}
	}

	// Recordings list counters (exercisesTotal/Done from /groups), recounted from the loaded exercises —
	// same rule as the server: generated, active, non-empty; done = every question answered by me.
	private static syncListProgress(audioId: number): AppThunk {
		return (dispatch, getState) => {
			const data = getAudioExercises(getState(), audioId);
			if (!data) return;
			const counted = data.exercises.filter(ex => ex.status !== EExerciseStatus.INACTIVE && !!ex.content.items.length);
			const done = counted.filter(ex => {
				const answered = new Set(data.myAnswers.filter(a => a.exerciseId === ex.id).map(a => a.questionIndex));
				return answered.size >= ex.content.items.length;
			}).length;
			const patch = { exercisesTotal: counted.length, exercisesDone: done };
			getGroups(getState())
				.filter(g => g.audios?.some(a => a.id === audioId))
				.forEach(g => dispatch(updateAudioAction(audioId, g.id, patch)));
		}
	}

	public static loadStudents(audioId: number): AppThunk {
		return async (dispatch) => {
			try {
				const { students, answers } = await ExercisesRest.fetchStudents(audioId);
				dispatch(setExerciseStudentsAction(audioId, students, answers));
			} catch (e) {
				console.error('ExercisesEffects.loadStudents failed', e);
			}
		}
	}

}
